/**
 * Tenant dashboard copy (COL-479).
 *
 * These two dictionaries used to sit inline in `Dashboard.tsx`, which is part
 * of why that file is ~10k lines. They are pure data, so they live here
 * instead: the page imports them, and `scripts/i18n-seed-tenant-copy.ts`
 * imports them too, to generate the migration that seeds every string into
 * `public.translations`.
 *
 * The English and German text below stays the built-in fallback. At runtime
 * `translateDictionary` (src/lib/i18n/dictionary.ts) looks each key up in the
 * translation table first, so the four languages COL-478 added render whatever
 * has been seeded or corrected by an admin, while de/en render exactly as they
 * did before when the table is empty or unreachable.
 *
 * Keep the two halves key-for-key identical: the seeding script pairs them by
 * key, and a key missing from the German half would seed an English-only row.
 */

export function getPaymentsLabels(lang: "en" | "de") {
  return lang === "de"
    ? {
        paymentOverview: "Zahlungsübersicht",
        paymentsOverviewEyebrow: "Übersicht",
        paymentsHeroSubtitle: "Miete, Kaution und Historie an einem Ort.",
        paymentsSectionAtAGlance: "Auf einen Blick",
        paymentsSectionDetails: "Miete & Kaution im Detail",
        paymentsSectionPaymentOptions: "Zahlungsoptionen",
        overviewPropertyLabel: "Objekt",
        overviewBillingPeriodLabel: "Abrechnungszeitraum",
        bankTransferSectionTitle: "Bankverbindung",
        bankTransferSectionSubtitle: "Überweisungen an dieses Konto.",
        proRataBadge: "Anteilig",
        paymentMethod: "Zahlungsmethode",
        rentBreakdown: "Mietaufstellung",
        paymentHistory: "Zahlungshistorie",
        monthlyRent: "Monatsmiete",
        monthlyRentDesc: "Grundmiete und Nebenkosten. Monatliche Abrechnung.",
        nextPayment: "Nächste Zahlung",
        usePayNowToSettle: (monthLabel: string) =>
          `Nutzen Sie „Jetzt zahlen“, um die Miete für ${monthLabel} sicher zu begleichen.`,
        nextRentDueNoAction: (date: string) =>
          `Nächste Miete fällig am ${date}. Bis dahin ist keine Aktion nötig.`,
        deposit: "Kaution",
        depositHeldRefunded:
          "Sicher verwahrt und am Ende des Aufenthalts zurückerstattet, abzüglich eventueller Abzüge.",
        rent: "Miete",
        monthlyRentUpper: "MONATSMIETE",
        room: "Zimmer",
        monthsOfRentPaid: "Monate Miete bezahlt",
        months: "Monate",
        paid: "bezahlt",
        dueOn: "Fällig am",
        depositStatus: "Kautionsstatus",
        oneTimeSecurityDeposit:
          "Einmalige Kaution, sicher für Ihren Aufenthalt verwahrt.",
        securityDeposit: "Kaution",
        expected: "Erwartet",
        amountPaid: "Bezahlt",
        paidOn: "Bezahlt am",
        depositActivity: "Kautionsaktivität",
        noDepositRecorded: "Noch keine Kautionszahlung erfasst.",
        depositOnlyEndOfTenancy:
          "Ihre Kaution wird nur am Ende der Mietzeit für vereinbarte Abzüge verwendet. Sie wird nicht auf die Monatsmiete angerechnet.",
        refreshPaymentStatus: "Zahlungsstatus aktualisieren",
        payDeposit: "Kaution zahlen",
        depositPayableWhenSigned:
          "Ihre Kaution wird fällig, sobald Ihr Vertrag unterzeichnet ist.",
        upcomingPayment: "Anstehende Zahlung",
        amountDueLabel: "Fälliger Betrag",
        reviewNextRentPaySecurely:
          "Prüfen Sie Ihre nächste Mietzahlung und zahlen Sie sicher mit Ihrer Karte.",
        upcomingInvoiceProRata: "Anteilige Miete (laut dieser Rechnung).",
        rentLabel: "Miete",
        paidStatus: "Bezahlt",
        overdueStatus: "Überfällig",
        pendingStatus: "Ausstehend",
        openStatus: "Offen",
        partiallyPaidStatus: "Teilweise bezahlt",
        paidOfBilled: (paid: string, billed: string) =>
          `${paid} von ${billed} bezahlt`,
        openRentLabel: "Offene Miete",
        dunningFeesLabel: "Mahngebühren",
        totalOpenLabel: "Gesamt offen",
        outstandingFeesLabel: "Offene Mahngebühren",
        rentFeeSplit: (rent: string, fees: string) =>
          `${rent} Miete · ${fees} Mahngebühren`,
        feesByTransferNote:
          "Karte und Lastschrift ziehen nur die Miete ein. Bitte überweisen Sie die Mahngebühren.",
        bankTransferFeesNote: (fees: string, total: string) =>
          `Zuzüglich ${fees} offene Mahngebühren – bitte insgesamt ${total} überweisen.`,
        sepaInProgressStatus: "SEPA-Lastschrift läuft",
        sepaInProgressNote:
          "Die Lastschrift wurde bei Ihrer Bank eingereicht und wird in 2–5 Bankarbeitstagen abgebucht. Sie müssen nichts weiter tun — wir bestätigen per E-Mail, sobald das Geld eingegangen ist.",
        payNow: "Jetzt zahlen",
        viewDetails: "Details anzeigen",
        paymentCompleted: "Zahlung abgeschlossen ✓",
        oneTimeDepositUpdates:
          "Einmalige Kaution. Betrag und Status werden bei erfassten Zahlungen aktualisiert.",
        dueWhenSignedPayBelow:
          "Fällig nach Vertragsunterzeichnung. Zahlen Sie unten, um abzuschließen.",
        depositHeldEndOfTenancy:
          "Dieser Betrag wird sicher verwahrt und am Ende Ihrer Mietzeit geprüft. Er wird nicht für die Monatsmiete verwendet.",
        manageHowCollected:
          "Verwalten Sie, wie Ihre Mietzahlungen eingezogen werden.",
        secure: "Sicher",
        cardPaymentStripe: "Kartenzahlung über Stripe",
        cardDetailsCheckout: "Kartendaten werden beim Checkout hinzugefügt.",
        stripeAdditionalChargesNoteTitle: "Hinweis",
        stripeAdditionalChargesNoteBody:
          "Bei Kartenzahlung über Stripe können zusätzliche Gebühren anfallen.",
        active: "Aktiv",
        managePaymentMethod: "Zahlungsmethode verwalten",
        last6Months: "Letzte 6 Monate",
        noPaymentsRecordedYet:
          "Noch keine Zahlungen erfasst. Sobald Ihre erste Zahlung verbucht ist, erscheint sie hier.",
        oneTime: "Einmalig",
        hereHowRentStructured:
          "So setzt sich Ihre Monatsmiete für Ihr aktuelles Zimmer zusammen.",
        backToOverview: "Zurück zur Übersicht",
        baseRent: "Grundmiete",
        furnishedPrivateRoom:
          "Ihr möbliertes Einzelzimmer in einer Wohngemeinschaft.",
        utilitiesServices: "Nebenkosten & Leistungen",
        electricityWaterWiFi:
          "Strom, Wasser, Heizung, WLAN und Reinigung der Gemeinschaftsflächen.",
        totalMonthlyRent: "Gesamtmiete monatlich",
        proRataFirstMonthNote: (_fullEuro: string) =>
          `Die erste Mietzahlung kann anteilig sein (nach Einzugsdatum). Der genaue Betrag steht bei der nächsten Zahlung.`,
        depositHeldSeparate: (amount: string) =>
          `Ihre Kaution in Höhe von ${amount} wird auf einem separaten Konto verwahrt und kann nicht für die Monatsmiete verwendet werden. Sie wird nach der Übergabe geprüft und ggf. zurückerstattet.`,
        nextCollectionDate: (date: string) =>
          `Nächster Einzugstermin: ${date}. Nach jeder erfolgreichen Zahlung erhalten Sie eine Quittung.`,
        collectionDate: "Einzugsdatum",
        receiptsHistory: "Belege & Verlauf",
        availableInPaymentHistory: "Unter „Zahlungshistorie“",
        cancel: "Abbrechen",
        confirmPayment: "Zahlung bestätigen",
        reviewAndConfirmPayment: "Zahlung prüfen & bestätigen",
        confirmPaymentTitle: "Zahlung bestätigen",
        confirmPaymentSubtitle:
          "Prüfen Sie die Zusammenfassung und bestätigen Sie anschließend, um Ihre anstehende Miete sicher per Karte über Stripe zu bezahlen.",
        firstRentCollection: "Erste Mietabbuchung",
        firstRentCollectionDesc:
          "Dies umfasst Ihre erste Monatsmiete. Kaution und ggf. Mitgliedsbeitrag werden separat berechnet, falls noch offen.",
        stripeProcessedStatement:
          "Ihre Zahlung wird sicher von Stripe verarbeitet. Die Belastung erscheint kurz nach der Bestätigung auf Ihrem Kontoauszug.",
        paymentSuccessful: "Zahlung erfolgreich",
        last12Payments: "Letzte 12 Zahlungen",
        allPastPaymentsHere:
          "Alle Ihre Miet- und Kautionszahlungen erscheinen hier, sobald sie erfasst sind.",
        paymentStatusRefreshed: "Zahlungsstatus aktualisiert.",
        payByBankTransfer: "Per Überweisung zahlen",
        uploadPaymentProof: "Zahlungsnachweis hochladen",
        uploadPaymentProofMandatory:
          "Bei Zahlung per Überweisung ist das Hochladen eines Zahlungsnachweises verpflichtend.",
        uploadStatement:
          "Kontoauszug oder Überweisungsbeleg hochladen (PDF oder Bild)",
        uploading: "Wird hochgeladen…",
        uploadSuccess:
          "Zahlungsnachweis wurde hochgeladen. Die Prüfung kann einen Moment dauern.",
        proofPendingReview: "Wird geprüft",
        proofApproved: "Angenommen",
        proofRejected: "Abgelehnt",
        rejectionReason: "Grund der Ablehnung",
        viewPaymentProof: "Zahlungsnachweis anzeigen",
        paymentProofUploaded: "Zahlungsnachweis hochgeladen",
        bankTransferNoAdditionalChargesNote:
          "Bei Überweisungen fallen keine zusätzlichen Gebühren an.",
        // Card fee disclosure
        cardFeeTitle: "Kartenzahlung – Gebühren",
        cardFeeSubtotal: "Zwischensumme",
        cardFeeProcessing: "Kartenzahlungsgebühr",
        cardFeeTotal: "Gesamtbetrag",
        cardFeeExplainer:
          "Die Stripe-Kartenzahlungsgebühr wird separat ausgewiesen, damit der volle Mietbetrag bei coliviq ankommt.",
        continueToStripe: "Weiter zu Stripe",
        // SEPA direct debit mandate
        payBySepa: "SEPA-Lastschriftmandat einrichten",
        sepaFormTitle: "SEPA-Basis-Lastschriftmandat",
        sepaCreditorHeading: "Gläubiger",
        sepaCreditorNameLabel: "Zahlungsempfänger",
        sepaCreditorIdLabel: "Gläubiger-ID",
        sepaMandateReferenceLabel: "Mandatsreferenz (UMR)",
        sepaMandateReferencePending: "Wird nach Signatur vergeben",
        sepaAuthorizationHeading: "SEPA-Basis-Lastschrift-Mandat",
        sepaAuthorizationText:
          "Ich ermächtige/Wir ermächtigen {creditor}, Zahlungen von meinem/unserem Konto mittels Lastschrift einzuziehen. Zugleich weise ich mein/weisen wir unser Kreditinstitut an, die von {creditor} auf mein/unser Konto gezogenen Lastschriften einzulösen.",
        sepaRefundNoticeHeading: "Hinweis",
        sepaRefundNoticeText:
          "Ich kann/Wir können innerhalb von acht Wochen, beginnend mit dem Belastungsdatum, die Erstattung des belasteten Betrags verlangen. Es gelten dabei die mit meinem/unserem Kreditinstitut vereinbarten Bedingungen.",
        sepaCustomerDataHeading: "Kundendaten",
        sepaSalutation: "Anrede",
        sepaMr: "Herr",
        sepaMrs: "Frau",
        sepaDivers: "Divers",
        sepaFirstName: "Vorname",
        sepaLastName: "Nachname",
        sepaStreet: "Straße",
        sepaHouseNumber: "Hausnr.",
        sepaPostalCode: "PLZ",
        sepaCity: "Ort",
        sepaCountry: "Land",
        sepaIban: "IBAN",
        sepaBic: "BIC (optional)",
        sepaAccountHolder: "Kontoinhaber",
        sepaSignatureHeading: "Unterschrift",
        sepaSigningPlace: "Ort",
        sepaSigningDate: "Datum",
        sepaConfirmCheckbox:
          "Ich bestätige die Mandatserteilung und stimme dem Einzug zu.",
        sepaTypedNameLabel: "Vollständiger Name zur Unterschrift",
        sepaTypedNameHelper:
          "Geben Sie Ihren vollständigen Namen genau so ein, wie oben (Vorname Nachname), um die Unterschrift zu bestätigen.",
        sepaSubmit: "Mandat erteilen",
        sepaSubmitting: "Mandat wird erteilt…",
        sepaMandatoryField: "Bitte füllen Sie alle Pflichtfelder aus.",
        sepaIbanInvalid: "Ungültige IBAN.",
        sepaTypedNameMismatch:
          "Der eingegebene Name stimmt nicht mit Vor- und Nachname überein.",
        sepaSuccessTitle: "SEPA-Mandat erteilt",
        sepaSuccessBody: (ref: string) =>
          `Ihr SEPA-Mandat ist aktiv. Mandatsreferenz: ${ref}`,
        sepaMandateActiveBadge: "SEPA aktiv",
        sepaActiveAutoDebitNote:
          "Die Miete wird zum Fälligkeitsdatum automatisch von deinem hinterlegten Konto eingezogen — du musst nichts weiter tun.",
        sepaAutoDebitPayNowNote:
          "Hinweis: Für dieses Konto ist ein SEPA-Lastschriftmandat aktiv — die Miete wird zum Fälligkeitsdatum automatisch eingezogen. Eine Kartenzahlung ist nur nötig, wenn du nicht auf den Einzug warten möchtest.",
        sepaGenericError: "Mandat konnte nicht gespeichert werden.",
      }
    : {
        paymentOverview: "Payment overview",
        paymentsOverviewEyebrow: "Overview",
        paymentsHeroSubtitle: "Rent, deposit, and history in one place.",
        paymentsSectionAtAGlance: "At a glance",
        paymentsSectionDetails: "Rent & deposit details",
        paymentsSectionPaymentOptions: "Payment options",
        overviewPropertyLabel: "Property",
        overviewBillingPeriodLabel: "Billing period",
        bankTransferSectionTitle: "Bank transfer details",
        bankTransferSectionSubtitle: "Use these details for SEPA transfers.",
        proRataBadge: "Pro-rata",
        paymentMethod: "Payment method",
        rentBreakdown: "Rent breakdown",
        paymentHistory: "Payment history",
        monthlyRent: "Monthly rent",
        monthlyRentDesc: "Includes base rent and services. Billed monthly.",
        nextPayment: "Next payment",
        usePayNowToSettle: (monthLabel: string) =>
          `Use Pay Now to settle the ${monthLabel} rent securely.`,
        nextRentDueNoAction: (date: string) =>
          `Next rent is due on ${date}. No action needed until then.`,
        deposit: "Deposit",
        depositHeldRefunded:
          "Held securely and refunded at the end of your stay, minus any deductions.",
        rent: "Rent",
        monthlyRentUpper: "MONTHLY RENT",
        room: "Room",
        monthsOfRentPaid: "Months of rent paid",
        months: "months",
        paid: "paid",
        dueOn: "Due on",
        depositStatus: "Deposit status",
        oneTimeSecurityDeposit:
          "One-time security deposit held safely for your stay.",
        securityDeposit: "Security deposit",
        expected: "Expected",
        amountPaid: "Amount paid",
        paidOn: "Paid on",
        depositActivity: "Deposit activity",
        noDepositRecorded: "No deposit payment recorded yet.",
        depositOnlyEndOfTenancy:
          "Your deposit is only used at the end of your tenancy to cover any agreed deductions. It is not applied towards monthly rent.",
        refreshPaymentStatus: "Refresh payment status",
        payDeposit: "Pay Deposit",
        depositPayableWhenSigned:
          "Your deposit becomes payable once your contract is signed.",
        upcomingPayment: "Upcoming payment",
        amountDueLabel: "Amount due",
        reviewNextRentPaySecurely:
          "Review your next rent payment and pay securely with your card.",
        upcomingInvoiceProRata: "Pro-rata amount for this invoice.",
        rentLabel: "Rent",
        paidStatus: "Paid",
        overdueStatus: "Overdue",
        pendingStatus: "Pending",
        openStatus: "Open",
        partiallyPaidStatus: "Partially paid",
        paidOfBilled: (paid: string, billed: string) =>
          `${paid} of ${billed} paid`,
        openRentLabel: "Rent open",
        dunningFeesLabel: "Late fees",
        totalOpenLabel: "Total open",
        outstandingFeesLabel: "Outstanding late fees",
        rentFeeSplit: (rent: string, fees: string) =>
          `${rent} rent · ${fees} late fees`,
        feesByTransferNote:
          "Card and direct debit collect the rent only. Please pay the late fees by bank transfer.",
        bankTransferFeesNote: (fees: string, total: string) =>
          `Plus ${fees} in outstanding late fees – please transfer ${total} in total.`,
        sepaInProgressStatus: "SEPA in progress",
        sepaInProgressNote:
          "The direct debit has been submitted to your bank and will be collected within 2–5 business days. Nothing else for you to do — we email you again once the money has arrived.",
        payNow: "Pay Now",
        viewDetails: "View details",
        paymentCompleted: "Payment Completed ✓",
        oneTimeDepositUpdates:
          "One-time security deposit. Amount and status update when payments are recorded.",
        dueWhenSignedPayBelow:
          "Due once contract is signed. Pay below to complete.",
        depositHeldEndOfTenancy:
          "This amount is held securely and will be reviewed at the end of your tenancy. It is not used to pay your monthly rent.",
        manageHowCollected: "Manage how your rent payments are collected.",
        secure: "Secure",
        cardPaymentStripe: "Card payment via Stripe",
        cardDetailsCheckout: "Card details are added during checkout.",
        stripeAdditionalChargesNoteTitle: "Note",
        stripeAdditionalChargesNoteBody:
          "Additional charges may apply when paying by card via Stripe.",
        active: "Active",
        managePaymentMethod: "Manage payment method",
        last6Months: "Last 6 months",
        noPaymentsRecordedYet:
          "No payments recorded yet. Once your first payment is collected, it will appear here.",
        oneTime: "One-time",
        hereHowRentStructured:
          "Here's how your monthly rent is structured for your current room.",
        backToOverview: "Back to overview",
        baseRent: "Base rent",
        furnishedPrivateRoom:
          "Your furnished private room in a shared apartment.",
        utilitiesServices: "Utilities & services",
        electricityWaterWiFi:
          "Electricity, water, heating, Wi‑Fi and shared space cleaning.",
        totalMonthlyRent: "Total monthly rent",
        proRataFirstMonthNote: (_fullEuro: string) =>
          `Your first rent invoice may be lower (pro-rata by move-in date). The exact amount appears on your upcoming payment.`,
        depositHeldSeparate: (amount: string) =>
          `Your deposit of ${amount} is held in a separate account and cannot be used to pay your monthly rent. It will be reviewed and potentially refunded after your move-out inspection.`,
        nextCollectionDate: (date: string) =>
          `Next collection date: ${date}. You'll receive a receipt after each successful payment.`,
        collectionDate: "Collection date",
        receiptsHistory: "Receipts & history",
        availableInPaymentHistory: 'Available in "Payment history"',
        cancel: "Cancel",
        confirmPayment: "Confirm payment",
        reviewAndConfirmPayment: "Review & confirm payment",
        confirmPaymentTitle: "Confirm your payment",
        confirmPaymentSubtitle:
          "Review the summary below, then confirm to pay your upcoming rent securely with your card via Stripe.",
        firstRentCollection: "First rent collection",
        firstRentCollectionDesc:
          "This includes your first month of rent. Your deposit will be charged separately if still outstanding.",
        stripeProcessedStatement:
          "Your payment is processed securely by Stripe. You'll see the charge on your statement shortly after confirmation.",
        paymentSuccessful: "Payment successful",
        last12Payments: "Last 12 payments",
        allPastPaymentsHere:
          "All of your past rent and deposit payments will appear here once recorded.",
        paymentStatusRefreshed: "Payment status refreshed.",
        payByBankTransfer: "Pay by bank transfer",
        uploadPaymentProof: "Upload payment proof",
        uploadPaymentProofMandatory:
          "When paying by direct bank transfer, uploading payment proof is mandatory.",
        uploadStatement:
          "Upload bank statement or transfer receipt (PDF or image)",
        uploading: "Uploading…",
        uploadSuccess: "Payment proof uploaded. Review may take a moment.",
        proofPendingReview: "Pending review",
        proofApproved: "Approved",
        proofRejected: "Rejected",
        rejectionReason: "Rejection reason",
        viewPaymentProof: "View payment proof",
        paymentProofUploaded: "Payment proof uploaded",
        bankTransferNoAdditionalChargesNote:
          "No additional charges apply for bank transfers.",
        // Card fee disclosure
        cardFeeTitle: "Card payment — processing fee",
        cardFeeSubtotal: "Subtotal",
        cardFeeProcessing: "Card processing fee",
        cardFeeTotal: "Total",
        cardFeeExplainer:
          "Stripe charges a card processing fee which is passed on so coliviq receives the full rent or deposit.",
        continueToStripe: "Continue to Stripe",
        // SEPA direct debit mandate
        payBySepa: "Set up SEPA Direct Debit",
        sepaFormTitle: "SEPA Direct Debit Mandate",
        sepaCreditorHeading: "Creditor",
        sepaCreditorNameLabel: "Payee",
        sepaCreditorIdLabel: "Creditor ID",
        sepaMandateReferenceLabel: "Mandate reference (UMR)",
        sepaMandateReferencePending: "Will be issued after signing",
        sepaAuthorizationHeading: "SEPA Core Direct Debit Mandate",
        sepaAuthorizationText:
          "I authorise {creditor} to send instructions to my bank to debit my account, and my bank to debit my account in accordance with the instructions from {creditor}.",
        sepaRefundNoticeHeading: "Notice",
        sepaRefundNoticeText:
          "As part of your rights, you are entitled to a refund from your bank under the terms and conditions of your agreement with your bank. A refund must be claimed within 8 weeks starting from the date on which your account was debited.",
        sepaCustomerDataHeading: "Customer details",
        sepaSalutation: "Salutation",
        sepaMr: "Mr",
        sepaMrs: "Ms",
        sepaDivers: "Divers",
        sepaFirstName: "First name",
        sepaLastName: "Last name",
        sepaStreet: "Street",
        sepaHouseNumber: "No.",
        sepaPostalCode: "Postal code",
        sepaCity: "City",
        sepaCountry: "Country",
        sepaIban: "IBAN",
        sepaBic: "BIC (optional)",
        sepaAccountHolder: "Account holder",
        sepaSignatureHeading: "Signature",
        sepaSigningPlace: "Place",
        sepaSigningDate: "Date",
        sepaConfirmCheckbox:
          "I confirm the mandate and authorise the direct debit.",
        sepaTypedNameLabel: "Full name for signature",
        sepaTypedNameHelper:
          "Type your full name exactly as entered above (First Last) to sign the mandate.",
        sepaSubmit: "Authorise mandate",
        sepaSubmitting: "Authorising mandate…",
        sepaMandatoryField: "Please fill in all mandatory fields.",
        sepaIbanInvalid: "Invalid IBAN.",
        sepaTypedNameMismatch:
          "The typed name does not match your first and last name.",
        sepaSuccessTitle: "SEPA mandate authorised",
        sepaSuccessBody: (ref: string) =>
          `Your SEPA mandate is active. Mandate reference: ${ref}`,
        sepaMandateActiveBadge: "SEPA active",
        sepaActiveAutoDebitNote:
          "Rent is debited automatically from your registered account on the due date — nothing else for you to do.",
        sepaAutoDebitPayNowNote:
          "Note: an active SEPA mandate is on file — rent is debited automatically on the due date. Paying by card is only needed if you don't want to wait for the debit.",
        sepaGenericError: "Could not save the mandate.",
      };
}

/** Home tab labels (Your rooms, status flow, stage names) – used when switching language */
export function getHomeLabels(lang: "en" | "de") {
  const en = {
    yourRooms: "Your rooms",
    statusFlow:
      "Status flow: Applied → Awaiting approval → Ready to sign → Contract signed → Deposit pending → Paid",
    stages: [
      "Applied",
      "Awaiting approval",
      "Ready to sign",
      "Contract signed",
      "Deposit pending",
      "Paid",
    ] as const,
    noRoomsYet:
      "No rooms yet. Your bookings will appear here with their current stage.",
    room: "room",
    rooms: "rooms",
    moveIn: "Move-in",
    moveOut: "Move-out",
    contractActionTitle: "Your contract needs attention",
    contractActionDesc:
      "Review and sign in DocuSign, then pay your security deposit on the Contract tab when it becomes due.",
    openMyContract: "Open My Contract",
    dateChangeNote:
      "You can update move-in and move-out dates here until your contract is signed. After signing, dates are fixed. The stay cannot be shorter than 6 months.",
    dateLockedNote:
      "Your contract is signed — move dates can no longer be changed here.",
    editDates: "Change dates",
    saveDates: "Save dates",
    moveInPicker: "Move-in date",
    moveOutPicker: "Move-out date",
    datesUpdated: "Stay dates updated.",
    dateValidationError: "Move-out must be at least six months after move-in.",
    stayDatesDialogTitle: "Your stay dates",
  };
  const de = {
    yourRooms: "Deine Zimmer",
    statusFlow:
      "Status: Beworben → Wartet auf Freigabe → Bereit zum Unterschreiben → Vertrag unterschrieben → Kaution ausstehend → Bezahlt",
    stages: [
      "Beworben",
      "Wartet auf Freigabe",
      "Bereit zum Unterschreiben",
      "Vertrag unterschrieben",
      "Kaution ausstehend",
      "Bezahlt",
    ] as const,
    noRoomsYet:
      "Noch keine Zimmer. Deine Buchungen erscheinen hier mit ihrem aktuellen Status.",
    room: "Zimmer",
    rooms: "Zimmer",
    moveIn: "Einzug",
    moveOut: "Auszug",
    contractActionTitle: "Dein Vertrag wartet auf dich",
    contractActionDesc:
      "Prüfe und unterschreibe in DocuSign; die Kaution zahlst du auf dem Tab „Mein Vertrag“, sobald sie fällig ist.",
    openMyContract: "Zu Mein Vertrag",
    dateChangeNote:
      "Ein- und Auszugsdatum können Sie hier ändern, bis Ihr Vertrag unterschrieben ist. Danach sind die Daten fest. Die Mindestmietdauer beträgt 6 Monate.",
    dateLockedNote:
      "Vertrag unterschrieben — die Daten können hier nicht mehr geändert werden.",
    editDates: "Daten ändern",
    saveDates: "Speichern",
    moveInPicker: "Einzugsdatum",
    moveOutPicker: "Auszugsdatum",
    datesUpdated: "Aufenthaltsdaten aktualisiert.",
    dateValidationError:
      "Auszug muss mindestens sechs Monate nach Einzug liegen.",
    stayDatesDialogTitle: "Ihre Aufenthaltsdaten",
  };
  return lang === "de" ? de : en;
}
